import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectUserRole, selectIsAuthenticated, selectUser } from '../../../store/slices/authSlice';
import { ROLES } from '../../../api/constants';
import ErrorLayout from '../components/ErrorLayout';
import { useThemeContext } from '../../../components/ThemeProvider';
import { ShieldX, ArrowLeft, Home, LogOut } from 'lucide-react';
import { useCommonTranslation } from '../../../hooks';

const Error403 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userRole = useSelector(selectUserRole);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector(selectUser);
  const { primaryColor } = useThemeContext();
  const { t } = useCommonTranslation();

  const attemptedPath = location.state?.from?.pathname || location.pathname;

  const getDefaultRoute = () => {
    if (!isAuthenticated) return '/login';
    return '/dashboard';
  };

  const handleGoBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate(getDefaultRoute(), { replace: true });
    }
  };

  const handleGoHome = () => {
    navigate(getDefaultRoute(), { replace: true });
  };

  const handleLogout = () => {
    navigate('/login', { replace: true });
  };

  const getRoleName = () => {
    switch (userRole) {
      case ROLES.ADMIN: return t('errorPages.403.roles.admin');
      case ROLES.TENANT_ADMIN: return t('errorPages.403.roles.tenantAdmin');
      case ROLES.USER: return t('errorPages.403.roles.user');
      default: return t('errorPages.403.roles.unknown');
    }
  };

  return (
    <ErrorLayout
      code={403}
      title={t('errorPages.403.title')}
      message={t('errorPages.403.message', { path: attemptedPath })}
      action={
        <div className="space-y-4">
          {isAuthenticated && user && (
            <div className="text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-lg p-3 mb-4">
              <div className="flex items-center justify-center gap-2">
                <ShieldX className="w-4 h-4" />
                <span
                  dangerouslySetInnerHTML={{
                    __html: t('errorPages.403.loggedInAs', { email: user.email, role: getRoleName() }),
                  }}
                />
              </div>
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button 
              onClick={handleGoBack}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-gray-700 dark:text-gray-200 font-medium border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('errorPages.403.goBack')}
            </button>
            <button 
              onClick={handleGoHome}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-white font-medium hover:opacity-90 transition-opacity"
              style={{ backgroundColor: primaryColor || '#1E40AF' }}
            >
              <Home className="w-4 h-4" />
              {t('errorPages.403.goDashboard')}
            </button>
          </div>
          
          {isAuthenticated && (
            <div className="pt-2">
              <button 
                onClick={handleLogout}
                className="inline-flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              >
                <LogOut className="w-3 h-3" />
                {t('errorPages.403.loginDifferent')}
              </button>
            </div>
          )}
        </div>
      }
    />
  );
};

export default Error403;
